import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Cpu, ExternalLink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { http, fmtDate } from "../../lib/api";

export const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PartSelect = ({ cat, value, onChange, socket }) => {
  const options = (cat.parts || []).filter((p) => cat.key !== "motherboard" || !socket || !p.socket || p.socket === socket);
  return (
    <div data-testid={`builder-row-${cat.key}`} className="min-w-0">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{cat.label}{!cat.required && " (optional)"}</p>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger data-testid={`builder-select-${cat.key}`} className="mt-2 h-auto min-h-[52px] w-full min-w-0 rounded-none border-white/10 bg-white/[0.03] px-4 py-3 text-left text-sm text-bone focus:ring-ember [&>span]:min-w-0 [&>span]:truncate">
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent className="max-h-80 rounded-none border-white/10 bg-ink text-bone">
          <SelectItem value="none">{cat.required ? "Choose…" : "None"}</SelectItem>
          {options.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`builder-option-${p.id}`}>
              {p.name} — {gbp(p.price)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const Summary = ({ lines, fee, pct, total, complete, onSend, amazonLive, checked }) => (
  <div className="min-w-0 border border-white/10 bg-slab/80 p-5 sm:p-6 lg:sticky lg:top-28" data-testid="builder-summary">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">Your estimate</p>
    <ul className="mt-4 space-y-2 text-sm">
      {lines.length === 0 && <li className="text-slate-400">Choose parts to see your estimate.</li>}
      {lines.map((l) => (
        <li key={l.cat} className="flex justify-between gap-4">
          <span className="min-w-0 text-slate-300">{l.name}{l.url && <a href={l.url} target="_blank" rel="noreferrer sponsored" className="ml-1.5 inline-block align-middle text-slate-400 hover:text-ember" aria-label={`View ${l.name} on Amazon`}><ExternalLink className="h-3 w-3" /></a>}</span>
          <span className="shrink-0 font-mono">{gbp(l.price)}</span>
        </li>
      ))}
    </ul>
    <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
      <div className="flex justify-between"><span className="text-slate-300">Parts</span><span className="font-mono" data-testid="builder-subtotal">{gbp(total - fee)}</span></div>
      <div className="flex justify-between"><span className="text-slate-300">Build fee{pct > 0 ? ` (${pct}%)` : ""}</span><span className="font-mono" data-testid="builder-fee">{fee > 0 ? gbp(fee) : pct > 0 ? gbp(0) : "Quoted separately"}</span></div>
    </div>
    <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
      <span className="font-display text-lg font-bold">Estimated total</span>
      <motion.span key={total} initial={{ scale: 1.15, color: "#FF5500" }} animate={{ scale: 1, color: "#F8FAFC" }} className="font-display text-3xl font-black" data-testid="builder-total">{gbp(total)}</motion.span>
    </div>
    <p className="mt-3 text-xs leading-relaxed text-slate-400">
      {amazonLive ? `Based on Amazon UK prices${checked ? ` checked ${fmtDate(checked)}` : ""}.` : "Based on estimated Amazon UK prices."} Prices change often. We confirm the final price in your quote.
    </p>
    <button type="button" data-testid="builder-send-btn" disabled={!complete} onClick={onSend}
      className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-ember px-5 py-3.5 font-semibold text-ink transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40">
      Send this build for a quote <ArrowDown className="h-4 w-4" />
    </button>
    {!complete && lines.length > 0 && <p className="mt-2 text-xs text-slate-400">Choose all required parts to continue.</p>}
    {amazonLive && <p className="mt-4 text-[11px] text-slate-500">As an Amazon Associate, Wilko Tech LTD earns from qualifying purchases.</p>}
  </div>
);

export const PcBuilder = ({ onSend }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  useEffect(() => {
    http.get("/builder").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  const cats = Array.isArray(data?.categories) ? data.categories : [];
  const byId = useMemo(() => Object.fromEntries(cats.flatMap((c) => c.parts.map((p) => [p.id, { ...p, cat: c.key }]))), [cats]);
  if (!data || !cats.some((c) => c.parts.length)) return null;

  const pct = Number(data.fee_percent) || 0;
  const cpuSocket = byId[sel.cpu]?.socket || "";
  const lines = cats.filter((c) => byId[sel[c.key]]).map((c) => ({ cat: c.key, name: byId[sel[c.key]].name, price: byId[sel[c.key]].price, url: byId[sel[c.key]].amazon_url }));
  const subtotal = lines.reduce((s, l) => s + l.price, 0);
  const fee = Math.round(((Number(data.build_fee) || 0) + (subtotal * pct) / 100) * 100) / 100;
  const total = subtotal + fee;
  const complete = cats.every((c) => !c.required || byId[sel[c.key]]);
  const checked = lines.map((l) => byId[sel[l.cat]].checked_at).filter(Boolean).sort()[0];
  const set = (k) => (v) => setSel((p) => {
    const next = { ...p, [k]: v };
    if (k === "cpu" && byId[p.motherboard]?.socket && byId[v]?.socket && byId[p.motherboard].socket !== byId[v].socket) next.motherboard = "";
    return next;
  });

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-testid="pc-builder">
      <div className="grid min-w-0 grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
        {cats.map((c) => (
          <PartSelect key={c.key} cat={c} value={sel[c.key]} onChange={set(c.key)} socket={c.key === "motherboard" ? cpuSocket : ""} />
        ))}
        <p className="flex items-start gap-2 text-xs text-slate-400 sm:col-span-2"><Cpu className="h-4 w-4 shrink-0 text-ember" /> Motherboards are filtered to match your processor.</p>
      </div>
      <div className="min-w-0 lg:col-span-5">
        <Summary lines={lines} fee={fee} pct={pct} total={total} complete={complete} amazonLive={data.amazon_live} checked={checked}
          onSend={() => onSend({ parts: sel, lines, fee, total })} />
      </div>
    </div>
  );
};
